import * as Sentry from '@sentry/react'

type SessionReplayConfig = {
  replaysSessionSampleRate: number
  replaysOnErrorSampleRate: number
  maskAllText: boolean
  blockAllMedia: boolean
  maskAllInputs: boolean
}

export const initLogging = (
  dsn: string,
  release: string,
  environment: string,
  sessionReplayConfig?: SessionReplayConfig
) => {
  if (!Sentry.isInitialized()) {
    Sentry.init({
      dsn,
      release,
      environment,
      normalizeDepth: 5,
      sendDefaultPii: true, // infer the IP address of users' devices to events (errors, traces, replays, etc)
      denyUrls: [
        // Browser extensions (moz, chrome)
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Klaviyo newsletter form issue
        /static-forms\.klaviyo\.com\/forms\/api\/v7\/[a-zA-Z0-9_.-]*\/full-forms/i,
      ],
      ignoreErrors: [
        // See https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
        'Non-Error promise rejection captured with value: Object Not Found Matching Id',
        'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
      ],
      /** Session Replay */
      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: sessionReplayConfig?.replaysSessionSampleRate,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: sessionReplayConfig?.replaysOnErrorSampleRate,

      integrations: [
        Sentry.replayIntegration({
          // Additional SDK configuration (see: https://docs.sentry.io/platforms/javascript/session-replay/privacy/)
          maskAllText: sessionReplayConfig?.maskAllText,
          blockAllMedia: sessionReplayConfig?.blockAllMedia,
          maskAllInputs: sessionReplayConfig?.maskAllInputs,
        }),
        Sentry.thirdPartyErrorFilterIntegration({
          // Specify the application keys that you specified in the Sentry bundler plugin
          filterKeys: ['all3dp-gmbh-craftcloud-client'],

          // Defines how to handle errors that contain third party stack frames.
          // Possible values are:
          // - 'drop-error-if-contains-third-party-frames'
          // - 'drop-error-if-exclusively-contains-third-party-frames'
          // - 'apply-tag-if-contains-third-party-frames'
          // - 'apply-tag-if-exclusively-contains-third-party-frames'
          behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
        }),
      ],
      beforeSend: event => {
        try {
          // Do not track errors from Google pagead/adsense (fielname found in raw Sentry event JSON in issue details)
          if (
            event.exception?.values?.at(0)?.stacktrace?.frames?.at(0)?.filename ===
            '/pagead/viewthroughconversion/957650798/'
          ) {
            return null
          }
        } catch {
          throw new Error('Sentry beforeSend failed')
        }

        return event
      },
    })

    // eslint-disable-next-line no-console
    console.info(`*** Sentry release: ${release} ***`)
  } else {
    Sentry.logger.warn('Sentry is already initialized')
  }
}

export const setTags = (tags: Record<string, string>) => {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value)
  })
}

export const setUserContext = (userId: string) =>
  Sentry.setUser({
    userId,
  })

export const setSessionContext = (state: Record<string, unknown>) => {
  Sentry.setContext('session', state)
}

export const captureException = (
  error: unknown,
  errorType = 'default',
  extra: Record<string, unknown> = {}
) =>
  Sentry.captureException(error, {
    tags: {
      error_type: errorType,
    },
    extra: {
      ...extra,
      error,
    },
  })

export const captureFatalException = (error: unknown, extra?: Record<string, unknown>) =>
  captureException(error, 'fatal', extra)

export const captureIgnoredException = (error: unknown, extra?: Record<string, unknown>) =>
  captureException(error, 'ignored', extra)

export const createSentryReduxEnhancer = Sentry.createReduxEnhancer
